/**
 * Export options for the Resource Histogram PDF.
 *
 * Everything the PDF prints is decided here: header title (editable, left or
 * centred), the letterhead job line, the filter cards (off / above / below the
 * chart, and which cards), the Figures strip (same off / above / below),
 * whether the chart stretches to fill the page, and
 * the footnote clauses. Choices are remembered per job.
 */
import React, { useEffect, useState } from 'react';

export const DEFAULT_PDF_CLAUSES = [
  'LGL reserves the right to level resources in accordance with project conditions and overall work availability.'
];

const CLAUSES_PREFIX = 'evm-pdf-clauses-v1';
const OPTIONS_PREFIX = 'evm-pdf-options-v1';

const clausesKey = (jobNumber) => `${CLAUSES_PREFIX}:${jobNumber || 'default'}`;
const optionsKey = (jobNumber) => `${OPTIONS_PREFIX}:${jobNumber || 'default'}`;

export const PDF_FILTER_MODES = ['off', 'right', 'top', 'bottom'];
export const PDF_FIGURE_MODES = ['off', 'top', 'bottom'];

/** Middle ground: sharper than 1.15× / JPEG 0.76, smaller than the old 1.5× PNG. */
export const PDF_SNAP_SCALE = 1.35;
export const PDF_JPEG_QUALITY = 0.88;
export const PDF_CHART_STRETCH_MAX = 2200;

export function pdfPrintsFilters(filtersMode, cardOptions = [], filterCards = {}) {
  if (!PDF_FILTER_MODES.includes(filtersMode) || filtersMode === 'off') return false;
  if (!cardOptions.length) return false;
  return cardOptions.some((card) => filterCards[card.id] !== false);
}

export function pdfChartDrawSize(canvasW, canvasH, boxW, boxH, { fill = true } = {}) {
  if (!(canvasW > 0 && canvasH > 0 && boxW > 0 && boxH > 0)) return { w: 0, h: 0 };
  if (fill) return { w: boxW, h: boxH };
  let drawW = boxW;
  let drawH = (canvasH * drawW) / canvasW;
  if (drawH > boxH) {
    drawH = boxH;
    drawW = (canvasW * drawH) / canvasH;
  }
  return { w: drawW, h: drawH };
}

export const DEFAULT_PDF_OPTIONS = {
  title: '',            // empty → the computed "estimate - name - report" default
  titleAlign: 'left',   // 'left' | 'center'
  showJobLine: true,    // letterhead job / client line under the logo
  filtersMode: 'right', // 'off' | 'right' | 'top' | 'bottom' — right matches the UI drawer
  figuresMode: 'top',   // 'off' | 'top' | 'bottom' — BAC / PV / EV strip
  filterCards: {},      // card id → false to leave that card out of the PDF
  chartFill: true,      // stretch the chart so it fills the free page height
  clauses: [...DEFAULT_PDF_CLAUSES]
};

/** Legacy clause store (pre-options). Kept as the seed for old jobs. */
export function loadPdfClauses(jobNumber) {
  try {
    const raw = localStorage.getItem(clausesKey(jobNumber));
    if (raw == null) return [...DEFAULT_PDF_CLAUSES];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map((c) => String(c));
  } catch { /* fall through to default */ }
  return [...DEFAULT_PDF_CLAUSES];
}

export function loadPdfOptions(jobNumber) {
  try {
    const raw = localStorage.getItem(optionsKey(jobNumber));
    if (raw != null) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          ...DEFAULT_PDF_OPTIONS,
          ...parsed,
          filterCards: { ...(parsed.filterCards || {}) },
          filtersMode: PDF_FILTER_MODES.includes(parsed.filtersMode) ? parsed.filtersMode : DEFAULT_PDF_OPTIONS.filtersMode,
          figuresMode: PDF_FIGURE_MODES.includes(parsed.figuresMode) ? parsed.figuresMode : DEFAULT_PDF_OPTIONS.figuresMode,
          clauses: Array.isArray(parsed.clauses) ? parsed.clauses.map((c) => String(c)) : [...DEFAULT_PDF_CLAUSES]
        };
      }
    }
  } catch { /* fall through */ }
  return { ...DEFAULT_PDF_OPTIONS, filterCards: {}, clauses: loadPdfClauses(jobNumber) };
}

export function savePdfOptions(jobNumber, options) {
  try {
    localStorage.setItem(optionsKey(jobNumber), JSON.stringify(options || {}));
  } catch { /* storage full or blocked — the download still works */ }
}

const linesToClauses = (text) => String(text || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean);

const sectionLabel = {
  fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.4
};

const checkLabel = {
  display: 'flex', alignItems: 'center', gap: 7,
  fontSize: '0.8rem', color: '#334155', cursor: 'pointer'
};

export default function HistogramPdfModal({
  open,
  jobNumber,
  defaultTitle,
  cardOptions = [],
  options,
  busy,
  onCancel,
  onDownload
}) {
  const [title, setTitle] = useState('');
  const [titleAlign, setTitleAlign] = useState('left');
  const [showJobLine, setShowJobLine] = useState(true);
  const [filtersMode, setFiltersMode] = useState('off');
  const [figuresMode, setFiguresMode] = useState('top');
  const [filterCards, setFilterCards] = useState({});
  const [chartFill, setChartFill] = useState(true);
  const [clauseText, setClauseText] = useState('');

  useEffect(() => {
    if (!open) return;
    const o = { ...DEFAULT_PDF_OPTIONS, ...(options || {}) };
    setTitle(String(o.title || ''));
    setTitleAlign(o.titleAlign === 'center' ? 'center' : 'left');
    setShowJobLine(o.showJobLine !== false);
    setFiltersMode(PDF_FILTER_MODES.includes(o.filtersMode) ? o.filtersMode : DEFAULT_PDF_OPTIONS.filtersMode);
    setFiguresMode(['off', 'top', 'bottom'].includes(o.figuresMode) ? o.figuresMode : 'top');
    setFilterCards({ ...(o.filterCards || {}) });
    setChartFill(o.chartFill !== false);
    setClauseText((o.clauses || []).join('\n'));
  }, [open, options]);

  if (!open) return null;

  const submit = () => {
    const next = {
      title: title.trim(),
      titleAlign,
      showJobLine,
      filtersMode,
      figuresMode,
      filterCards,
      chartFill,
      clauses: linesToClauses(clauseText)
    };
    savePdfOptions(jobNumber, next);
    onDownload(next);
  };

  const btn = {
    height: 32,
    padding: '0 14px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer'
  };

  const segBtn = (active) => ({
    ...btn,
    height: 26,
    padding: '0 10px',
    fontSize: '0.72rem',
    background: active ? '#0f766e' : '#fff',
    color: active ? '#fff' : '#374151',
    border: `1px solid ${active ? '#0f766e' : '#d1d5db'}`
  });

  const cardOn = (id) => filterCards[id] !== false;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
      }}
    >
      <div style={{
        width: 'min(680px, 96vw)', maxHeight: '92vh', overflowY: 'auto',
        background: '#fff', borderRadius: 10, boxShadow: '0 20px 60px rgba(15,23,42,0.3)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '0.85rem 1rem', borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
            Download PDF
          </h3>
          <button type="button" onClick={onCancel} disabled={busy} style={{ ...btn, border: 'none', fontSize: '1.1rem', padding: '0 8px' }}>
            ×
          </button>
        </div>

        <div style={{ padding: '1rem' }}>
          {/* ---- Header title ---- */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <label htmlFor="evm-pdf-title" style={sectionLabel}>Header title</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={() => setTitleAlign('left')} style={segBtn(titleAlign === 'left')}>
                Left
              </button>
              <button type="button" onClick={() => setTitleAlign('center')} style={segBtn(titleAlign === 'center')}>
                Centre
              </button>
            </div>
          </div>
          <input
            id="evm-pdf-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={defaultTitle}
            style={{
              width: '100%', marginTop: 6, padding: '7px 10px', height: 32,
              border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box',
              fontSize: '0.8rem', color: '#0f172a', fontWeight: 600
            }}
          />
          <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#94a3b8' }}>
            Leave empty for the standard title: {defaultTitle}
          </div>

          {/* ---- Layout ---- */}
          <div style={{ ...sectionLabel, marginTop: 16 }}>Layout</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 6 }}>
            <label style={checkLabel}>
              <input
                type="checkbox"
                checked={showJobLine}
                onChange={(e) => setShowJobLine(e.target.checked)}
                style={{ accentColor: '#0f766e', width: 14, height: 14 }}
              />
              Job / client line under the logo (job number, client, project type)
            </label>
            <label style={checkLabel}>
              <input
                type="checkbox"
                checked={chartFill}
                onChange={(e) => setChartFill(e.target.checked)}
                style={{ accentColor: '#0f766e', width: 14, height: 14 }}
              />
              Stretch the chart to fill the free page (full page when filters are off)
            </label>
          </div>

          {/* ---- Filters ---- */}
          <div style={{ ...sectionLabel, marginTop: 16 }}>Filters on the PDF</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {[['off', 'Off'], ['right', 'Right of the chart'], ['top', 'Above the chart'], ['bottom', 'Below the chart']].map(([mode, label]) => (
              <button key={mode} type="button" onClick={() => setFiltersMode(mode)} style={segBtn(filtersMode === mode)}>
                {label}
              </button>
            ))}
          </div>
          {filtersMode !== 'off' && (
            <div style={{
              marginTop: 8, padding: '8px 10px', maxHeight: 170, overflowY: 'auto',
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  Cards to print — the list mirrors what is on the Filters pane right now.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const allOn = cardOptions.every((c) => cardOn(c.id));
                    const next = {};
                    if (allOn) cardOptions.forEach((c) => { next[c.id] = false; });
                    setFilterCards(next);
                  }}
                  style={{ ...btn, height: 22, padding: '0 8px', fontSize: '0.68rem' }}
                >
                  {cardOptions.every((c) => cardOn(c.id)) ? 'None' : 'All'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                {cardOptions.map((c) => (
                  <label key={c.id} style={{ ...checkLabel, fontSize: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={cardOn(c.id)}
                      onChange={(e) => setFilterCards((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                      style={{ accentColor: '#0f766e', width: 13, height: 13, flex: 'none' }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.label}>
                      {c.label}
                    </span>
                  </label>
                ))}
                {!cardOptions.length && (
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    No filter cards are visible — open Filters and add slicers first.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ---- Figures ---- */}
          <div style={{ ...sectionLabel, marginTop: 16 }}>Figures on the PDF</div>
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            {[['off', 'Off'], ['top', 'Above the chart'], ['bottom', 'Below the chart']].map(([mode, label]) => (
              <button key={mode} type="button" onClick={() => setFiguresMode(mode)} style={segBtn(figuresMode === mode)}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.4 }}>
            The BAC / PV / EV / SPI strip. Off, above, or below the chart.
          </div>

          {/* ---- Footnotes ---- */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
            <label htmlFor="evm-pdf-clauses" style={sectionLabel}>
              Footnotes — one clause per line
            </label>
            <button
              type="button"
              onClick={() => setClauseText(DEFAULT_PDF_CLAUSES.join('\n'))}
              style={{ ...btn, height: 24, padding: '0 8px', fontSize: '0.7rem' }}
            >
              Restore default
            </button>
          </div>
          <textarea
            id="evm-pdf-clauses"
            value={clauseText}
            onChange={(e) => setClauseText(e.target.value)}
            rows={4}
            placeholder="Add a clause per line. Leave empty for no footnotes."
            style={{
              width: '100%', marginTop: 6, padding: '8px 10px',
              border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: '0.8rem', lineHeight: 1.45, color: '#334155',
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
            }}
          />
          <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.4 }}>
            Clauses print under the chart, above the printed timestamp and the page link.
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb'
        }}>
          <button type="button" onClick={onCancel} disabled={busy} style={{ ...btn, opacity: busy ? 0.55 : 1 }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={{
              ...btn,
              background: '#0f766e', border: '1px solid #0f766e', color: '#fff',
              opacity: busy ? 0.55 : 1, cursor: busy ? 'wait' : 'pointer'
            }}
          >
            {busy ? 'Building PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
